import React, { useState, useEffect } from 'react';
import { Zap, ZapOff, Mail, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * InstantNotificationsConfig - Component to manage instant email notifications
 * for new calls matching user's areas of interest
 */
const InstantNotificationsConfig = ({ token }) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Load current status on mount
  useEffect(() => {
    const loadStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/notifications/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setEnabled(data.instant_notifications_enabled || false);
        }
      } catch (error) {
        console.error('Error loading notification settings:', error);
      }
      setLoading(false);
    };

    if (token) {
      loadStatus();
    }
  }, [token]);

  const handleToggle = async (newValue) => {
    setEnabled(newValue);
    setSaving(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/notifications/settings/instant?enabled=${newValue}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ 
          type: 'success', 
          text: data.message 
        });
      } else {
        throw new Error('Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating notification settings:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar preferências. Tente novamente.' });
      setEnabled(!newValue); // Revert on error
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="instant-notifications-config">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 rounded-full p-2">
            <Zap className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Notificações Instantâneas</h3>
            <p className="text-sm text-white/80">Receba alertas assim que novas chamadas forem publicadas</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {enabled ? (
              <Zap className="h-5 w-5 text-amber-500" />
            ) : (
              <ZapOff className="h-5 w-5 text-gray-400" />
            )}
            <div>
              <div className="font-medium text-gray-900">
                {enabled ? 'Notificações ativadas' : 'Notificações desativadas'}
              </div>
              <div className="text-sm text-gray-500">
                {enabled 
                  ? 'Você receberá email quando novas chamadas forem publicadas na sua área' 
                  : 'Ative para ser notificado imediatamente sobre novas chamadas'}
              </div>
            </div>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => handleToggle(e.target.checked)}
              disabled={saving}
              className="sr-only peer"
              data-testid="instant-notifications-toggle"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-100 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
          </label>
        </div>

        {/* Info box when enabled */}
        {enabled && (
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
            <div className="flex gap-2">
              <Mail className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-medium text-amber-700">Como funciona?</p>
                <ul className="mt-1 space-y-1 list-disc list-inside text-gray-600">
                  <li>Quando uma nova chamada for publicada, verificamos suas áreas de interesse</li>
                  <li>Se a chamada for relevante para você, enviamos um email imediatamente</li>
                  <li>Suas áreas de interesse são definidas no seu perfil acadêmico acima</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Message */}
        {message && (
          <div className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800' 
              : 'bg-red-50 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <Check className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstantNotificationsConfig;
